import { AllowedEventOptions, AnalyticsEvent } from './types';
import { eventsApiPost } from './api';
/**
 * An event tracking class for measuring anonymous events within the Chat SDK
 *
 * Counting events should be named following this pattern:
 *
 *    [location].[subject_with_underscores].[action]
 *
 * Example:
 *
 *    footer.chat_button.click
 *    footer.chat_button.keypress
 *
 * Timer events should be very descriptive. Our APIs have a generated output.
 *
 *    api.[method].[clean_endpoint].timer
 *
 * Example:
 *
 *    api.get.p_v3_kb_categories_root.timer
 *
 * But user-defined timers should follow this pattern:
 *
 *    [location].[description_with_underscores].[action]
 *
 * Example:
 *
 *    app.app_initialization.timer
 *    kb.kb_search.timer
 *
 */
export class EventTracking {
  events: AnalyticsEvent[] = [];

  runningTimers = new Map();

  orgName;

  constructor(orgName) {
    setInterval(() => {
      this.pushData();
    }, 3000);

    this.orgName = orgName;
  }

  setOrgName(orgName) {
    this.orgName = orgName;
  }

  pushData() {
    if (this.events.length === 0) {
      return;
    }

    if (this.orgName) {
      if (
        this.orgName === 'kustomer' ||
        this.orgName.startsWith('zzz-') ||
        this.orgName.startsWith('mikerapin')
      ) {
        // Push to API endpoint
        eventsApiPost(JSON.stringify(this.events), this.orgName);
      }

      this.events = [];
    }
  }

  createEvent(eventOptions: AllowedEventOptions) {
    return {
      surface: 'web',
      timestamp: Date.now(),
      ...eventOptions,
    };
  }

  /**
   * Used to count the number of triggers of an event. Events can be singular (default to 1) or can be manually set
   *  to be numerous using the EventContext count option
   */
  countEvent(name: string, count = 1, context = '') {
    if (count < 1) {
      return;
    }

    this.events.push(
      this.createEvent({
        context,
        count,
        name,
      }),
    );
  }

  timeEventStart(name: string) {
    this.runningTimers.set(name, Date.now());
  }

  timeEventStop(name: string, context = '') {
    if (!this.runningTimers.has(name)) {
      console.error(`There is no timer event being tracked with name: ${name}`);
    }

    const time = this.runningTimers.get(name);

    this.events.push(
      this.createEvent({
        name,
        duration: Date.now() - time,
        context,
      }),
    );
  }
}
